/**
 * Unauthenticated WebSocket Hook
 * Handles WebSocket connection for anonymous CAD upload users
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createUnauthWebSocket, setAccessSecretCookie } from '../api/cadUploadApi'
import { WS_MAX_RECONNECT_ATTEMPTS, WS_RECONNECT_DELAY_MS } from '../utils/constants'
import { type ProcessingStatusData, type UnauthWebsocketMessage } from '../utils/types'
import LogTool from '../../../logger/logTools'

const log = new LogTool({ context: 'useUnauthWebsocket', enable: true, logLevel: 'debug' })

interface UseUnauthWebsocketOptions {
  accessSecret: string | null
  onSCSFileReady?: () => void
  onQuotationReady?: () => void
  onQuotationLoading?: () => void
  onPropertiesReady?: (data?: UnauthWebsocketMessage['data']) => void
  onProcessingStatus?: (data?: ProcessingStatusData) => void
  onError?: (error: Error) => void
  onFallbackToPolling?: () => void
}

interface UseUnauthWebsocketReturn {
  isConnected: boolean
  lastMessage: UnauthWebsocketMessage | null
  connect: () => void
  disconnect: () => void
  send: (data: string | ArrayBufferLike | Blob | ArrayBufferView) => void
}

export function useUnauthWebsocket(
  options: UseUnauthWebsocketOptions
): UseUnauthWebsocketReturn {
  const {
    accessSecret,
    onSCSFileReady,
    onQuotationReady,
    onQuotationLoading,
    onPropertiesReady,
    onProcessingStatus,
    onError,
    onFallbackToPolling,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const [lastMessage, setLastMessage] = useState<UnauthWebsocketMessage | null>(null)
  
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectCountRef = useRef(0)
  const shouldReconnectRef = useRef(true)

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string) as UnauthWebsocketMessage
        log.info('Received WebSocket message:', message)
        setLastMessage(message)

        if (message.type === 'guest_request_scs_file_notification') {
          onSCSFileReady?.()
        } else if (message.type === 'guest_request_quotation_notification') {
          onQuotationReady?.()
        } else if (message.type === 'guest_request_quotation_loading_notification') {
          onQuotationLoading?.()
        } else if (message.type === 'guest_request_properties_notification') {
          onPropertiesReady?.(message.data)
        } else if (message.type === 'guest_request_processing_status_notification') {
          onProcessingStatus?.(message.data as ProcessingStatusData | undefined)
        }
      } catch (error) {
        log.error('Error parsing WebSocket message:', error)
      }
    },
    [
      onSCSFileReady,
      onQuotationReady,
      onQuotationLoading,
      onPropertiesReady,
      onProcessingStatus,
    ]
  )

  const connect = useCallback(() => {
    if (!accessSecret) {
      log.warn('Cannot connect WebSocket: no access secret')
      return
    }

    shouldReconnectRef.current = true

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      log.debug('WebSocket already connected or connecting')
      return
    }

    // Set the access secret cookie (for same-origin fallback)
    setAccessSecretCookie(accessSecret)

    try {
      // Pass access_secret as query parameter for cross-origin support
      const socket = createUnauthWebSocket(accessSecret)

      // Handle initial connection failure - if socket closes immediately after error
      // we should fall back to polling
      let checkInitialFailure: NodeJS.Timeout | null = null
      
      socket.onopen = () => {
        if (checkInitialFailure) {
          clearTimeout(checkInitialFailure)
          checkInitialFailure = null
        }
        log.info('WebSocket connected')
        setIsConnected(true)
        reconnectCountRef.current = 0
      }
      

      socket.onclose = (event) => {
        if (checkInitialFailure) {
          clearTimeout(checkInitialFailure)
          checkInitialFailure = null
        }
        setIsConnected(false)

        if (event.wasClean) {
          log.info('WebSocket closed cleanly:', event.code)
          return
        }

        log.warn('WebSocket disconnected unexpectedly')

        if (shouldReconnectRef.current && reconnectCountRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectCountRef.current += 1
          log.info(`Attempting reconnect ${reconnectCountRef.current}/${WS_MAX_RECONNECT_ATTEMPTS}`)
          
          setTimeout(() => {
            if (shouldReconnectRef.current) {
              connect()
            }
          }, WS_RECONNECT_DELAY_MS)
        } else if (reconnectCountRef.current >= WS_MAX_RECONNECT_ATTEMPTS) {
          log.warn('Max reconnection attempts reached, falling back to polling')
          onFallbackToPolling?.()
        }
      }

      socket.onerror = (event) => {
        log.error('WebSocket error:', event)
        onError?.(new Error('WebSocket connection error'))
        // Check if connection failed initially (after a short delay)
        if (!checkInitialFailure) {
          checkInitialFailure = setTimeout(() => {
            if (socket.readyState === WebSocket.CLOSED) {
              log.warn('WebSocket failed to connect initially, falling back to polling')
              onFallbackToPolling?.()
            }
          }, 2000) // Wait 2 seconds to see if connection succeeds
        }
      }

      socket.onmessage = handleMessage

      wsRef.current = socket
    } catch (error) {
      log.error('Failed to create WebSocket:', error)
      onError?.(error instanceof Error ? error : new Error('Failed to create WebSocket'))
    }
  }, [accessSecret, handleMessage, onError, onFallbackToPolling])

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false
    
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    
    setIsConnected(false)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  const send = useCallback((data: string | ArrayBufferLike | Blob | ArrayBufferView): void => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected. Cannot send message.')
    }
    wsRef.current.send(data)
  }, [])

  return {
    isConnected,
    lastMessage,
    connect,
    disconnect,
    send,
  }
}
